import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Input, Select } from 'antd';
import { DeleteOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';

import GetField from '../Components/GetField';
import styles from '../styles';

const AntSelect = GetField(Select);
const AntInput = GetField(Input);
const { Option } = Select;

const FabricForm = props => {
  const {
    classes,
    getValues,
    setValue,
    control,
    watch,
    fabricItem,
    fabricIndex,
    fabricAppend,
    fabricRemove,
    bpoNo,
  } = props;

  const colorIds = useSelector(state => state.colorIds);
  const fabricsTable = useSelector(
    state => state.rpoItemsForStoreOutwardInputTable.fabrics
  );
  const fabricStocksForOutward = useSelector(
    state => state.fabricStocksForOutward
  );

  const fabricsArray = useMemo(() => {
    if (fabricsTable.productNames.length) {
      const productId = getValues(`fabrics[${fabricIndex}].productId`);

      const productIndex = fabricsTable.productIndexes[productId || null];

      if (productIndex !== undefined) {
        return fabricsTable.fabrics[productIndex].fabricNames || [];
      }
    }
    return [];
  }, [fabricsTable, watch(`fabrics[${fabricIndex}].productId`)]);

  const widthsArray = useMemo(() => {
    if (fabricsTable.fabrics.length) {
      const fabricName = getValues(`fabrics[${fabricIndex}].fabric`);

      const productId = getValues(`fabrics[${fabricIndex}].productId`);

      const productIndex = fabricsTable.productIndexes[productId || null];

      if (fabricsTable.fabrics[productIndex]?.widths !== undefined) {
        return fabricsTable.fabrics[productIndex].widths[fabricName] || [];
      }
    }
    return [];
  }, [
    fabricsTable,
    watch(`fabrics[${fabricIndex}].fabric`),
    watch(`fabrics[${fabricIndex}].productId`),
  ]);

  const baseColorsArray = useMemo(() => {
    if (fabricsTable.fabrics.length) {
      const fabricId = getValues(`fabrics[${fabricIndex}].fabricId`);

      const productId = getValues(`fabrics[${fabricIndex}].productId`);

      const productIndex = fabricsTable.productIndexes[productId || null];

      if (fabricsTable.fabrics[productIndex]?.baseColors !== undefined) {
        return fabricsTable.fabrics[productIndex].baseColors[fabricId] || [];
      }
    }
    return [];
  }, [
    fabricsTable,
    watch(`fabrics[${fabricIndex}].fabricId`),
    watch(`fabrics[${fabricIndex}].productId`),
  ]);

  const printColorsArray = useMemo(() => {
    if (fabricsTable.fabrics.length) {
      const fabricId = getValues(`fabrics[${fabricIndex}].fabricId`);

      const productId = getValues(`fabrics[${fabricIndex}].productId`);

      const baseColorId = getValues(`fabrics[${fabricIndex}].baseColorId`);

      const productIndex = fabricsTable.productIndexes[productId || null];

      if (
        fabricsTable.fabrics[productIndex]?.printColors[fabricId] !== undefined
      ) {
        return (
          fabricsTable.fabrics[productIndex].printColors[fabricId][
            baseColorId
          ] || []
        );
      }
    }
    return [];
  }, [
    fabricsTable,
    watch(`fabrics[${fabricIndex}].fabricId`),
    watch(`fabrics[${fabricIndex}].productId`),
    watch(`fabrics[${fabricIndex}].baseColorId`),
  ]);

  const factoryArray = useMemo(() => {
    return (
      fabricStocksForOutward.factories[
        `P${watch(`fabrics[${fabricIndex}].productId`) || ''}F${watch(
          `fabrics[${fabricIndex}].fabricId`
        )}B${watch(`fabrics[${fabricIndex}].fromBaseColorId`)}P${
          watch(`fabrics[${fabricIndex}].fromPrintColorId`) || ''
        }`
      ] || []
    );
  }, [
    fabricStocksForOutward.factories,
    watch(`fabrics[${fabricIndex}].fabricId`),
    watch(`fabrics[${fabricIndex}].productId`),
    watch(`fabrics[${fabricIndex}].fromBaseColorId`),
    watch(`fabrics[${fabricIndex}].fromPrintColorId`),
  ]);

  const rackArray = useMemo(() => {
    return (
      fabricStocksForOutward.racknos[
        `P${watch(`fabrics[${fabricIndex}].productId`) || ''}F${watch(
          `fabrics[${fabricIndex}].fabricId`
        )}B${watch(`fabrics[${fabricIndex}].fromBaseColorId`)}P${
          watch(`fabrics[${fabricIndex}].fromPrintColorId`) || ''
        }F${watch(`fabrics[${fabricIndex}].factoryId`)}`
      ] || []
    );
  }, [
    fabricStocksForOutward.racknos,
    watch(`fabrics[${fabricIndex}].fabricId`),
    watch(`fabrics[${fabricIndex}].productId`),
    watch(`fabrics[${fabricIndex}].fromBaseColorId`),
    watch(`fabrics[${fabricIndex}].fromPrintColorId`),
    watch(`fabrics[${fabricIndex}].factoryId`),
  ]);

  useEffect(() => {
    setValue(
      `fabrics[${fabricIndex}].inventoryQuantity`,
      fabricStocksForOutward.stocks[
        `P${watch(`fabrics[${fabricIndex}].productId`) || ''}F${watch(
          `fabrics[${fabricIndex}].fabricId`
        )}B${watch(`fabrics[${fabricIndex}].fromBaseColorId`)}P${
          watch(`fabrics[${fabricIndex}].fromPrintColorId`) || ''
        }F${watch(`fabrics[${fabricIndex}].factoryId`)}R${watch(
          `fabrics[${fabricIndex}].racknoId`
        )}`
      ] || 0
    );
  }, [
    fabricStocksForOutward.stocks,
    watch(`fabrics[${fabricIndex}].fabricId`),
    watch(`fabrics[${fabricIndex}].productId`),
    watch(`fabrics[${fabricIndex}].fromBaseColorId`),
    watch(`fabrics[${fabricIndex}].fromPrintColorId`),
    watch(`fabrics[${fabricIndex}].factoryId`),
    watch(`fabrics[${fabricIndex}].racknoId`),
  ]);

  return (
    <tr>
      {bpoNo ? (
        <td className={classes.inputTd}>
          <Controller
            name={`fabrics[${fabricIndex}].productId`}
            control={control}
            defaultValue={fabricItem.productId}
            render={({ field }) => (
              <AntSelect
                placeholder="Select product"
                {...field}
                onChange={value => {
                  field.onChange(value);
                  setValue(`fabrics[${fabricIndex}].fabric`, null);
                  setValue(`fabrics[${fabricIndex}].fabricId`, null);
                  setValue(`fabrics[${fabricIndex}].fromBaseColorId`, null);
                  setValue(`fabrics[${fabricIndex}].fromPrintColorId`, null);
                  setValue(`fabrics[${fabricIndex}].baseColorId`, null);
                  setValue(`fabrics[${fabricIndex}].printColorId`, null);
                  setValue(`fabrics[${fabricIndex}].factoryId`, null);
                  setValue(`fabrics[${fabricIndex}].racknoId`, null);
                }}
              >
                {fabricsTable.productNames.map(({ product, id }) => (
                  <Option key={id} value={id}>
                    {product}
                  </Option>
                ))}
              </AntSelect>
            )}
          />
        </td>
      ) : null}
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].fabric`}
          control={control}
          defaultValue={fabricItem.fabric}
          render={({ field }) => (
            <AntSelect
              placeholder="Select fabric"
              {...field}
              onChange={value => {
                field.onChange(value);
                setValue(`fabrics[${fabricIndex}].fabricId`, null);
                setValue(`fabrics[${fabricIndex}].fromBaseColorId`, null);
                setValue(`fabrics[${fabricIndex}].fromPrintColorId`, null);
                setValue(`fabrics[${fabricIndex}].baseColorId`, null);
                setValue(`fabrics[${fabricIndex}].printColorId`, null);
                setValue(`fabrics[${fabricIndex}].factoryId`, null);
                setValue(`fabrics[${fabricIndex}].racknoId`, null);
              }}
            >
              {fabricsArray.map(fabric => (
                <Option key={fabric} value={fabric}>
                  {fabric}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].fabricId`}
          control={control}
          defaultValue={fabricItem.fabricId}
          render={({ field }) => (
            <AntSelect
              placeholder="Select width"
              {...field}
              onChange={value => {
                field.onChange(value);
                setValue(`fabrics[${fabricIndex}].fromBaseColorId`, null);
                setValue(`fabrics[${fabricIndex}].fromPrintColorId`, null);
                setValue(`fabrics[${fabricIndex}].baseColorId`, null);
                setValue(`fabrics[${fabricIndex}].printColorId`, null);
                setValue(`fabrics[${fabricIndex}].factoryId`, null);
                setValue(`fabrics[${fabricIndex}].racknoId`, null);
              }}
            >
              {widthsArray.map(width => (
                <Option key={width.id} value={width.id}>
                  {width.width}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].fromBaseColorId`}
          control={control}
          defaultValue={fabricItem.fromColorId}
          render={({ field }) => (
            <AntSelect
              placeholder="Select color"
              {...field}
              onChange={value => {
                field.onChange(value);
                setValue(`fabrics[${fabricIndex}].factoryId`, null);
                setValue(`fabrics[${fabricIndex}].racknoId`, null);
              }}
            >
              {colorIds.map(color => (
                <Option key={color.id} value={color.id}>
                  {color.color}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].fromPrintColorId`}
          control={control}
          defaultValue={fabricItem.fromColorId}
          render={({ field }) => (
            <AntSelect
              placeholder="Select color"
              {...field}
              onChange={value => {
                field.onChange(value);
                setValue(`fabrics[${fabricIndex}].factoryId`, null);
                setValue(`fabrics[${fabricIndex}].racknoId`, null);
              }}
            >
              {colorIds.map(color => (
                <Option key={color.id} value={color.id}>
                  {color.color}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].baseColorId`}
          control={control}
          defaultValue={fabricItem.baseColorId}
          render={({ field }) => (
            <AntSelect placeholder="Select color" {...field}>
              {baseColorsArray.map(color => (
                <Option key={color.id} value={color.id}>
                  {color.color}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].printColorId`}
          control={control}
          defaultValue={fabricItem.printColorId}
          render={({ field }) => (
            <AntSelect placeholder="Select color" {...field}>
              {printColorsArray.map(color => (
                <Option key={color.id} value={color.id}>
                  {color.color}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].factoryId`}
          control={control}
          defaultValue={fabricItem.factoryId}
          render={({ field }) => (
            <AntSelect
              placeholder="Select factory"
              {...field}
              onChange={value => {
                field.onChange(value);
                setValue(`fabrics[${fabricIndex}].racknoId`, null);
              }}
            >
              {factoryArray.map(factory => (
                <Option key={factory.id} value={factory.id}>
                  {factory.name}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].racknoId`}
          control={control}
          defaultValue={fabricItem.racknoId}
          render={({ field }) => (
            <AntSelect placeholder="Selecy rack" {...field}>
              {rackArray.map(rack => (
                <Option key={rack.id} value={rack.id}>
                  {rack.name}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].inventoryQuantity`}
          control={control}
          defaultValue={fabricItem.inventoryQuantity}
          render={({ field }) => (
            <AntInput
              {...field}
              placeholder="Inventory Qty"
              disabled
              type="number"
              min={0}
              size="small"
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].quantity`}
          control={control}
          defaultValue={fabricItem.quantity}
          render={({ field }) => (
            <AntInput
              {...field}
              placeholder="Quantity"
              type="number"
              min={0}
              size="small"
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].rate`}
          control={control}
          render={({ field }) => (
            <AntInput
              {...field}
              placeholder="Rate"
              type="number"
              min={0}
              size="small"
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].gst`}
          control={control}
          render={({ field }) => (
            <AntInput
              {...field}
              placeholder="GST(%)"
              type="number"
              min={0}
              size="small"
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].packages`}
          control={control}
          defaultValue={fabricItem.packages}
          render={({ field }) => <AntInput placeholder="Packages" {...field} />}
        />
      </td>
      <td className={classes.inputTd}>
        <DeleteOutlined
          onClick={() => {
            fabricRemove(fabricIndex);
          }}
        />
        &nbsp; &nbsp;
        <PlusCircleOutlined onClick={() => fabricAppend({ productId: null })} />
      </td>
    </tr>
  );
};

FabricForm.propTypes = {
  classes: PropTypes.object.isRequired,
  getValues: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  control: PropTypes.any.isRequired,
  watch: PropTypes.func.isRequired,
  fabricRemove: PropTypes.func.isRequired,
  fabricAppend: PropTypes.func.isRequired,
  fabricItem: PropTypes.object.isRequired,
  fabricIndex: PropTypes.number.isRequired,
  bpoNo: PropTypes.string,
};

export default withStyles(styles)(FabricForm);
